import { useRef } from "react";
import { animate } from "animejs";
import { FaRobot, FaBrain, FaLayerGroup, FaCube } from "react-icons/fa";
import useAnimeScope from "../../hooks/useAnimeScope";
import useAnimeReveal from "../../hooks/useAnimeReveal";
import AnimatedNumber from "../AnimatedNumber/AnimatedNumber";
import "./About.scss";

const cards = [
  {
    icon: <FaRobot />,
    title: "AI Automation Architect",
    description:
      "Design and deploy multi-agent orchestration systems using n8n, FastAPI, and LLM toolchains. Focused on OCR validation, document intelligence, and workflow reliability."
  },
  {
    icon: <FaBrain />,
    title: "RAG & LLM Engineer",
    description:
      "Build production RAG pipelines with PGVector, semantic chunking, retrieval tools, and multimodal AI integrations across Gemini, Mistral, and OpenAI style APIs."
  },
  {
    icon: <FaLayerGroup />,
    title: "Systems Integrator",
    description:
      "Connect CRMs, calling tools, WhatsApp, Google Workspace, e-commerce platforms, Docker workloads, event-driven services, and custom Frappe/ERPNext apps into one usable operating layer."
  },
  {
    icon: <FaCube />,
    title: "Frappe / ERPNext Developer",
    description:
      "Build custom Frappe & ERPNext apps with modular architectures, custom DocTypes, role-based portals, Dockerized deployments, and CI/CD pipelines on Coolify."
  }
];

const stats = [
  { number: "15+", label: "Client Ecosystems" },
  { number: "11+", label: "Production Systems" },
  { number: "3+", label: "Years Building AI" },
  { number: "100%", label: "Delivery Focus" }
];

const About = () => {
  const rootRef = useRef(null);

  useAnimeReveal(rootRef, "[data-reveal]", []);

  useAnimeScope(rootRef, () => {
    const cards = rootRef.current.querySelectorAll(".about-card");

    cards.forEach((card) => {
      const icon = card.querySelector(".about-card__icon");

      const handleEnter = () => {
        animate(icon, {
          rotate: [0, -8, 8, 0],
          scale: [1, 1.08],
          duration: 520,
          ease: "outElastic(1, .7)"
        });
      };

      card.addEventListener("mouseenter", handleEnter);

      card._animeCleanup = () => {
        card.removeEventListener("mouseenter", handleEnter);
      };
    });

    return () => {
      cards.forEach((card) => card._animeCleanup?.());
    };
  }, []);

  return (
    <section id="about" className="about" ref={rootRef}>
      <div className="about__content">
        <h2 className="about__heading" data-reveal>About Me</h2>

        <div className="about__intro" data-reveal>
          <p>
            I build the boring parts that make AI useful in real businesses:
            clean workflows, stable integrations, document pipelines, and systems
            that teams can actually operate after the demo hype is gone.
          </p>
        </div>

        <div className="about__cards">
          {cards.map((card) => (
            <article className="about-card" key={card.title} data-reveal>
              <div className="about-card__icon">{card.icon}</div>
              <h3>{card.title}</h3>
              <p>{card.description}</p>
            </article>
          ))}
        </div>

        <div className="about__stats" data-reveal>
          {stats.map((stat) => (
            <div className="stat" key={stat.label}>
              <AnimatedNumber value={stat.number} className="stat__number" />
              <div className="stat__label">{stat.label}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default About;
